import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { WishlistProvider } from './context/WishlistContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { PropertyFavoritesProvider } from './context/PropertyFavoritesContext';
import { AdsProvider } from './context/AdsContext';
import { CacheProvider } from './context/CacheContext';
import { TodoProvider } from './context/TodoContext';
import { NotesProvider } from './context/NotesContext';
import { NewsProvider } from './context/NewsContext';
import { PropertyCreatorProvider } from './context/PropertyCreatorContext';
import LoadingSpinner from './components/LoadingSpinner';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import PageAds from './components/ads/PageAds';
import AdManager from './components/ads/AdManager';
import ModalsWrapper from './components/ModalsWrapper';
import StickyNote from './components/common/StickyNote';
import ScrollToTop from './components/common/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy load pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const StocksPage = React.lazy(() => import('./pages/StocksPage'));
const WishlistPage = React.lazy(() => import('./pages/WishlistPage'));
const FundsWishlistPage = React.lazy(() => import('./pages/FundsWishlistPage'));
const StocksWishlistPage = React.lazy(() => import('./pages/StocksWishlistPage'));
const MyQuotesPage = React.lazy(() => import('./pages/MyQuotesPage'));
const FundDetailsPage = React.lazy(() => import('./pages/FundDetailsPage'));
const StockDetailsPage = React.lazy(() => import('./pages/StockDetailsPage'));
const RealEstatePage = React.lazy(() => import('./pages/RealEstatePage'));
const PropertyDetailsPage = React.lazy(() => import('./pages/PropertyDetailsPage'));
const FavoritePropertiesPage = React.lazy(() => import('./pages/FavoritePropertiesPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const FinancialGoalsPage = React.lazy(() => import('./pages/FinancialGoalsPage'));
const BlogsPage = React.lazy(() => import('./pages/BlogsPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const DownloadPageComponent = React.lazy(() => import('./pages/DownloadPage'));
//const NewsPage = React.lazy(() => import('./pages/NewsPage'));
// const NewsArticlePage = React.lazy(() => import('./pages/NewsArticlePage'));
const RSSAllNewsPage = React.lazy(() => import('./pages/RSSAllNewsPage').then(m => ({ default: m.RSSAllNewsPage })));
const RSSNewsDetailsPage = React.lazy(() => import('./pages/RSSNewsDetailsPage').then(m => ({ default: m.RSSNewsDetailsPage })));

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <CurrencyProvider>
          <WishlistProvider>
            <PropertyFavoritesProvider>
              <PropertyCreatorProvider>
                <TodoProvider>
                  <NotesProvider>
                    <NewsProvider>
                      <ThemeProvider>
                        <AdsProvider>
                          <CacheProvider>
                            <AdManager />
                            <div className="min-h-screen flex flex-col bg-theme-background text-theme-text">
                              <StickyNote />
                              <Navigation />
                              <main className="flex-grow">
                                <Suspense fallback={<LoadingSpinner />}>
                                  <Routes>
                                    <Route path="/" element={<HomePage />} />
                                    <Route path="/about" element={<AboutPage />} />
                                    <Route path="/search-funds" element={<SearchPage />} />
                                    <Route path="/search-stocks" element={<StocksPage />} />
                                    <Route path="/real-estate" element={<RealEstatePage />} />
                                    <Route path="/real-estate/:id" element={<PropertyDetailsPage />} />
                                    <Route path="/news" element={<RSSAllNewsPage />} />
                                    <Route path="/news/:id" element={<RSSNewsDetailsPage />} />
                                    {/* <Route path="/news-old" element={<NewsPage />} /> */}
                                    {/* <Route path="/news-old/:id" element={<NewsArticlePage />} /> */}
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/signup" element={<SignupPage />} />
                                    <Route path="/terms" element={<TermsPage />} />
                                    <Route path="/contact" element={<ContactPage />} />
                                    <Route path="/blog" element={<BlogsPage />} />
                                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                                    <Route path="/admin" element={<AdminPage />} />
                                    <Route path="/download" element={<DownloadPageComponent />} />
                                    <Route path="/fund/:schemeCode" element={<FundDetailsPage />} />
                                    <Route path="/stock/:symbol" element={<StockDetailsPage />} />
                                  <Route
                                    path="/profile"
                                    element={
                                      <ProtectedRoute>
                                        <ProfilePage />
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/wishlist"
                                    element={
                                      <ProtectedRoute>
                                        <WishlistPage />
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/wishlist/funds"
                                    element={
                                      <ProtectedRoute>
                                        <FundsWishlistPage />
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/wishlist/stocks"
                                    element={
                                      <ProtectedRoute>
                                        <StocksWishlistPage />
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/favorite-properties"
                                    element={
                                      <ProtectedRoute>
                                        <FavoritePropertiesPage />
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/my-quotes"
                                    element={
                                      <ProtectedRoute>
                                        <MyQuotesPage />
                                      </ProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="/financial-goals"
                                    element={
                                      <ProtectedRoute>
                                        <FinancialGoalsPage />
                                      </ProtectedRoute>
                                    }
                                  />
                                </Routes>
                              </Suspense>
                            </main>
                            <PageAds />
                            <Footer />
                            <ModalsWrapper />
                            <ScrollToTop />
                          </div>
                          <Toaster position="top-right" />
                          </CacheProvider>
                        </AdsProvider>
                      </ThemeProvider>
                    </NewsProvider>
                  </NotesProvider>
                </TodoProvider>
              </PropertyCreatorProvider>
            </PropertyFavoritesProvider>
          </WishlistProvider>
        </CurrencyProvider>
      </AuthProvider>
    </Router>
  );
}